import { useState } from 'react';
import { BookOpen, Plus, Archive, ArchiveRestore, Pencil, Search } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Input, Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ListRow } from '@/components/ui/ListRow';
import { cn } from '@/lib/cn';
import { SidePanel } from '@/components/ui/SidePanel';
import { Markdown } from '@/components/ui/Markdown';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { confirm } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { useAsync } from '@/lib/useAsync';
import { timeAgo } from '@/lib/timeAgo';
import { parseTags } from '@/lib/tags';
import { api, ApiError, type KnowledgeEntry } from '@/lib/api';

/**
 * One learnings surface, three scopes. The knowledge cascade (company →
 * workspace → project) renders through this single component; each tab passes
 * a source describing where its entries live. Charter handling stays in the
 * workspace tab — this panel is LEARNING entries only.
 */
export interface LearningsSource {
  /** Cache key — re-fetches when it changes. */
  key: string;
  list: (archived: boolean) => Promise<KnowledgeEntry[]>;
  create: (body: { title: string; body: string; tags?: string[] }) => Promise<unknown>;
  /** Shown on rows + empty state, e.g. "company-wide" / "engagement" / "workstream". */
  scopeLabel: string;
  emptyDescription: string;
}

type LearningsSort = 'newest' | 'oldest' | 'title';

export function LearningsPanel({ source, canEdit }: { source: LearningsSource; canEdit: boolean }) {
  const [editor, setEditor] = useState<{ entry: KnowledgeEntry | null } | null>(null);
  const [readerId, setReaderId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<LearningsSort>('newest');
  const [showArchived, setShowArchived] = useState(false);
  const entries = useAsync(() => source.list(showArchived), [source.key, showArchived]);

  async function setArchived(entry: KnowledgeEntry, archived: boolean) {
    if (archived) {
      const ok = await confirm({
        title: `Archive “${entry.title}”?`,
        description: 'Agents stop applying it on their next run.',
      });
      if (!ok) return;
    }
    try {
      await api.updateKnowledge(entry.id, { archived });
      toast.success(archived ? 'Archived' : 'Restored');
      entries.reload();
    } catch (err) {
      toast.danger(archived ? 'Could not archive' : 'Could not restore', err instanceof ApiError ? err.message : undefined);
    }
  }

  if (entries.status === 'loading') return <SkeletonRows rows={3} />;
  if (entries.status === 'error') return <ErrorState error={entries.error} onRetry={entries.reload} />;

  const q = query.trim().toLowerCase();
  const learnings = entries.data
    .filter((e) => e.kind === 'LEARNING' && e.archived === showArchived)
    .filter((e) => !q || e.title.toLowerCase().includes(q) || e.body.toLowerCase().includes(q))
    .sort((a, b) =>
      sort === 'title'
        ? a.title.localeCompare(b.title)
        : sort === 'oldest'
          ? a.createdAt.localeCompare(b.createdAt)
          : b.createdAt.localeCompare(a.createdAt),
    );
  const reader = learnings.find((e) => e.id === readerId) ?? null;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold">
          Learnings <span className="text-xs font-normal text-fg-subtle">{learnings.length}</span>
        </h2>
        <div className="relative ml-2">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search learnings"
            className="w-52 pl-8"
          />
        </div>
        <Select value={sort} onChange={(e) => setSort(e.target.value as LearningsSort)} className="w-auto">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="title">By title</option>
        </Select>
        {canEdit && (
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition',
              showArchived ? 'border-primary bg-primary-muted text-primary' : 'border-border text-fg-muted hover:text-fg',
            )}
          >
            Archived
          </button>
        )}
        {canEdit && (
          <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setEditor({ entry: null })}>
            <Plus className="h-4 w-4" /> Add learning
          </Button>
        )}
      </div>

      {learnings.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title={showArchived ? 'No archived learnings' : q ? 'No matches' : 'No learnings yet'}
          description={showArchived ? 'Entries you archive appear here and can be restored.' : q ? 'Try a different search.' : source.emptyDescription}
        />
      ) : (
        <ul className="divide-y divide-border">
          {learnings.map((e) => (
            <ListRow
              key={e.id}
              icon={<BookOpen className="h-4 w-4" />}
              title={e.title}
              meta={`Updated ${timeAgo(e.updatedAt)}`}
              actions={
                <>
                  {e.tags.map((t) => (
                    <Badge key={t} tone="neutral">{t}</Badge>
                  ))}
                  <Badge tone="neutral">{source.scopeLabel}</Badge>
                  <Badge tone={e.origin === 'AGENT_APPROVED' ? 'primary' : 'neutral'}>
                    {e.origin === 'AGENT_APPROVED' ? 'agent · approved' : 'staff'}
                  </Badge>
                  {canEdit && !e.archived && (
                    <>
                      <IconButton label="Edit" onClick={() => setEditor({ entry: e })}>
                        <Pencil className="h-4 w-4" />
                      </IconButton>
                      <IconButton label="Archive" onClick={() => setArchived(e, true)}>
                        <Archive className="h-4 w-4" />
                      </IconButton>
                    </>
                  )}
                  {canEdit && e.archived && (
                    <IconButton label="Restore" onClick={() => setArchived(e, false)}>
                      <ArchiveRestore className="h-4 w-4" />
                    </IconButton>
                  )}
                </>
              }
              onClick={() => setReaderId(e.id)}
            />
          ))}
        </ul>
      )}

      {reader && (
        <SidePanel
          title={reader.title}
          subtitle={
            <span className="flex items-center gap-2">
              <Badge tone="neutral">{source.scopeLabel}</Badge>
              <span className="text-xs text-fg-subtle">Updated {timeAgo(reader.updatedAt)}</span>
            </span>
          }
          onClose={() => setReaderId(null)}
        >
          <Markdown className="text-sm">{reader.body}</Markdown>
        </SidePanel>
      )}
      {editor && (
        <LearningEditorPanel
          source={source}
          entry={editor.entry}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            entries.reload();
          }}
        />
      )}
    </div>
  );
}

function LearningEditorPanel({
  source,
  entry,
  onClose,
  onSaved,
}: {
  source: LearningsSource;
  entry: KnowledgeEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(entry?.title ?? '');
  const [body, setBody] = useState(entry?.body ?? '');
  const [tagsRaw, setTagsRaw] = useState(entry?.tags.join(', ') ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const payload = { title: title.trim(), body, tags: parseTags(tagsRaw) };
    try {
      if (entry) await api.updateKnowledge(entry.id, payload);
      else await source.create(payload);
      toast.success(entry ? 'Saved' : 'Learning added');
      onSaved();
    } catch (err) {
      toast.danger('Could not save the learning', err instanceof ApiError ? err.message : undefined);
      setBusy(false);
    }
  }

  return (
    <SidePanel
      title={entry ? 'Edit learning' : 'New learning'}
      subtitle={<Badge tone="neutral">{source.scopeLabel}</Badge>}
      onClose={onClose}
    >
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Title" htmlFor="lrn-title">
          <Input id="lrn-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </Field>
        <Field label="Tags" htmlFor="lrn-tags" hint="Comma-separated, up to 12. Used for browsing and filtering.">
          <Input id="lrn-tags" value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} placeholder="vip, payments" />
        </Field>
        <Field label="Body" htmlFor="lrn-body" hint="Markdown. Agents read this verbatim on every run in scope.">
          <textarea
            id="lrn-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={16}
            required
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm leading-relaxed text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </Field>
        <Button type="submit" disabled={busy || !title.trim() || !body.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </SidePanel>
  );
}
